import '../../product-card/product-card-display';
import store from '../../../store/store';
import { BaseComponent } from '../../base-component/base-component';
import { StoreState } from '../../../store/store.interface';
import { booleanConverter } from '../../../common/converters';
import { connect } from 'pwa-helpers';
import { createMessage } from '../../../common/utils/util';
import { customElement, property, state } from 'lit/decorators.js';
import { html, TemplateResult } from 'lit';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { isCreateSearchSuccess, selectCreateSearchTotal } from './store/create-search.selector';
import { selectCreateRequest } from './store/create-search.selector';

@customElement('mhecomm-create-search-pagination-message')
export class CreateSearchPaginationMessage extends connect(store)(BaseComponent) {
  /**
   * The message to display to the user when there are no results. The following placeholders are available: %first% %last% %total% %term%
   */
  @property()
  emptyMessage = '0 Results Matching "%term%"';

  /**
   * The message to display to the user when there are results. The following placeholders are available: %first% %last% %total% %term%
   */
  @property()
  message = 'Showing %first%-%last% of %total% Results';

  /**
   * The message to display to the user when no term was provided by a %term% appears in the message property.
   */
  @property()
  noTermMessage = 'Showing %first%-%last% of %total% Results';

  /**
   * Region Id should be '-' seperated as this will be used as region name.
   * Example: results-bar will be labeled as 'results bar'
   */
  @property()
  regionid = '';

  /**
   * The message should be displayed without the compiled version.
   * Use what is being passed on the property value.
   */
  @property({ type: Boolean, converter: booleanConverter })
  useRawMessage = false;

  @state()
  private compiledMessage: HTMLElement | undefined;

  @state()
  private compiledEmptyMessage: HTMLElement | undefined;

  private readonly onSkipToResults = (e: Event) => this.focusRegion(e);

  connectedCallback(): void {
    super.connectedCallback();
    if (this.regionid) {
      this.addTrackedEventListener(window, 'skip-to-results', this.onSkipToResults);
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
  }

  update(changedProperties: any): void {
    super.update(changedProperties);
  }

  stateChanged(state: StoreState): void {
    if (isCreateSearchSuccess(state)) {
      // Use the adjusted total which automatically subtracts blocked count from Redux store
      const total = selectCreateSearchTotal(state) || 0;
      const request = selectCreateRequest(state) ?? undefined;
      const term = request?.searchValue ?? '';
      const start = request?.start ? request.start : 1;
      const maxEnd = (request?.start ? request.start : 1) + (request?.pageSize ? request?.pageSize - 1 : 0);
      const end = maxEnd > total ? total : maxEnd;

      if (total > 0) {
        this.compiledEmptyMessage = undefined;

        if (!term && this.message.includes('%term%')) {
          this.compiledMessage = this.compileMessage(this.noTermMessage, start, end, total, term);
        } else {
          this.compiledMessage = this.compileMessage(this.message, start, end, total, term);
        }
      } else {
        this.compiledMessage = undefined;
        this.compiledEmptyMessage = this.compileMessage(this.emptyMessage, start, end, total, term);
      }
    }
  }

  renderComp(): TemplateResult {
    return html`
      <div
        tabindex=${ifDefined(this.regionid ? '-1' : undefined)}
        role=${ifDefined(this.regionid ? 'region' : undefined)}
        aria-label=${ifDefined(this.regionid ? this.regionid.split('-').join(' ') : undefined)}
        data-automation-id=${ifDefined(this.regionid ? this.regionid : undefined)}
        id=${ifDefined(this.regionid ? this.regionid : undefined)}
      >
        <div aria-live="polite" role="status" aria-atomic="true">
          ${this.useRawMessage ? this.message : ''}
          ${this.compiledMessage && !this.useRawMessage ? this.compiledMessage : ''}
          ${this.compiledEmptyMessage && !this.useRawMessage ? this.compiledEmptyMessage : ''}
        </div>
      </div>
    `;
  }

  compileMessage(message: string, start: number, end: number, total: number, term: string): HTMLElement {
    const values = {
      first: start.toLocaleString(),
      last: end.toLocaleString(),
      total: total.toLocaleString(),
      term: term,
    };

    return createMessage(message, values);
  }

  focusRegion(e: Event): void {
    const event = e as CustomEvent;
    if (this.regionid === event.detail.target) {
      this.shadowRoot?.getElementById(this.regionid)?.focus();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-create-search-pagination-message': CreateSearchPaginationMessage;
  }
}
